/**
 * The 4x4 ordered-Bayer threshold, as one TSL node the whole port shares.
 *
 * It was written for `screen-sprites.ts`'s stipple fade and now has four
 * callers: that pool, `three/particle-cloud.ts`'s fire and smoke,
 * `three/searchlight-beams.ts`' falloff, and `flight/enhanced-sky.ts`, which
 * dithers a smooth sky gradient into the 6-bit VGA DAC the original wrote to.
 * Moved here rather than copied, on the 2026-08-21 bullet-rendering rule: a
 * second implementation of a stated rule is a place for a bug to hide with a
 * good explanation attached.
 *
 * **Sharing it is load-bearing, not tidiness.** `screen-sprites.ts`'s header
 * states the rule every drawn effect in this port obeys — no pool opts out of
 * the depth buffer, a fade is expressed as a stipple — so a stipple is the
 * *only* way a new blended-looking effect can be added to this renderer. Two
 * such effects overlapping had better be dissolving on exactly the same
 * threshold, or they stipple against each other and moiré.
 *
 * ## Why 6-bit quantisation is period-correct, not a filter
 *
 * `BARON.PAL`'s baked bytes are VGA DAC values widened from 6 bits — index 1's
 * `#203449` is `(8, 13, 18) << 2 | >> 4`, and every entry in the file has that
 * shape. A 1990 sky was therefore made of levels 4/255 apart, and a modern
 * smooth gradient over the same colours has values the hardware of the day
 * could not have addressed. `quantiseToVgaDac` puts them back on the grid, and
 * ordered-dithering the rounding is what the era's artists did with the
 * remainder.
 */

import * as THREE from "three/webgpu";

/**
 * A scalar TSL node. Spelled through `dot` because most of TSL's float-valued
 * exports are overloaded per component count, so `ReturnType` of one of those
 * resolves to whichever overload is declared last (`vec4`) rather than to the
 * scalar; `dot` has exactly one signature and it returns `Node<"float">`.
 */
export type FloatNode = ReturnType<typeof THREE.TSL.dot>;

/**
 * Half a step of the 4x4 ladder, added to every threshold so the darkest cell
 * is `1/32` rather than `0` — without it `step(0, 0)` is 1 in WGSL and a fully
 * faded sprite would keep one pixel in sixteen alight forever.
 */
export const DITHER_FLOOR = 1 / 32;

/** Levels per channel in a VGA DAC register: 6 bits. */
export const VGA_DAC_LEVELS = 64;

/**
 * The 4x4 ordered-Bayer threshold at this fragment's screen position, in
 * `[1/32, 1)`.
 *
 * `bayer2(c) = fract(x/2 + y*y*3/4)` is the closed form of the 2x2 matrix
 * `[0 2 / 3 1] / 4`, and the 4x4 one is the standard
 * `bayer2(c/2)/4 + bayer2(c)` recursion. Both cells are packed into one `vec4`
 * — coarse in `xy`, fine in `zw` — so the whole ladder is four `dot`s of a
 * weight vector: `@types/three` types no swizzle accessors on a TSL node (the
 * same reason `screen-sprites.ts`'s UV rect is two `vec2`s and not one
 * `vec4`), and a `dot` against a selector is what a swizzle would have been.
 */
export function ditherThreshold(): FloatNode {
  const coord = THREE.TSL.screenCoordinate;
  const cells = THREE.TSL.vec4(THREE.TSL.floor(coord.mul(0.5)), THREE.TSL.floor(coord));
  const squares = cells.mul(cells);
  const coarse = THREE.TSL.fract(
    THREE.TSL.dot(cells, THREE.TSL.vec4(0.5, 0, 0, 0)).add(THREE.TSL.dot(squares, THREE.TSL.vec4(0, 0.75, 0, 0))),
  );
  const fine = THREE.TSL.fract(
    THREE.TSL.dot(cells, THREE.TSL.vec4(0, 0, 0.5, 0)).add(THREE.TSL.dot(squares, THREE.TSL.vec4(0, 0, 0, 0.75))),
  );
  return coarse.mul(0.25).add(fine).add(DITHER_FLOOR);
}

/**
 * Rounds a `vec3` colour onto the 6-bit DAC grid, carrying the rounding
 * remainder into the ordered-dither pattern so a gradient shallower than one
 * DAC step stipples across two levels instead of showing a band edge.
 *
 * `floor(c * 63 + threshold) / 63`: the threshold is the fractional part's
 * decision boundary, so a channel `0.3` of the way to the next level lights
 * three cells of the ten... which is the whole of the technique.
 */
export function quantiseToVgaDac(colour: THREE.Node<"vec3">): THREE.Node<"vec3"> {
  const levels = VGA_DAC_LEVELS - 1;
  return THREE.TSL.floor(colour.mul(levels).add(ditherThreshold())).div(levels);
}
